import { useEmbarcacaoTanques } from "@/modules/embarcacoes";
import type { EmbarcacaoTanques, EmbarcacaoTanquesField } from "@/modules/embarcacoes";
import type { EmptyStateContent } from "@/screens/control-panel/components/empty-state";
import {
  DetailField,
  DetailSection,
  DetailSectionFields,
  DetailSectionState,
} from "../detail-section";
import { formatDetailDate, formatDetailNumber } from "../embarcacao-detail-format";
import type { EmbarcacaoDetailSectionProps } from "../embarcacao-detail-section";

export const TANQUES_ID = "tanques";
export const TANQUES_TITLE = "Tanques";
export const TANQUES_LOADING_MESSAGE = "Carregando os Tanques da embarcação...";

/** Padrão global de estado vazio do Hub para a seção sem informações (AC4). */
const TANQUES_EMPTY_STATE: EmptyStateContent = {
  title: "Nenhuma informação de Tanques disponível",
  description:
    "Assim que houver capacidades de tanques para esta embarcação, elas aparecerão nesta seção.",
};

const TEST_ID = "embarcacao-tanques";

interface TanquesFieldDefinition {
  readonly key: EmbarcacaoTanquesField;
  /** Rótulo do protótipo, já com a unidade de medida quando ela é prevista. */
  readonly label: string;
  /** Tratamento do valor textual; hoje só a data de atualização da capacidade. */
  readonly format?: (value: string) => string;
}

/**
 * Os 10 campos da seção, na ordem do AC2 — as 8 capacidades em m³, os
 * comentários e a data de atualização da capacidade. O grid os distribui em
 * duas colunas, preenchendo a linha da esquerda para a direita; nenhum campo
 * precisa abrir linha nesta seção.
 */
export const TANQUES_FIELDS: TanquesFieldDefinition[] = [
  { key: "capacidadeTotalCombustivel", label: "Capacidade Total de Combustível (m³)" },
  { key: "capacidadeTotalAgua", label: "Capacidade Total de Água (m³)" },
  { key: "capacidadeTotalOro", label: "Capacidade Total de ORO (m³)" },
  { key: "capacidadeTotalAguaLastro", label: "Capacidade Total de Água de Lastro (m³)" },
  { key: "capacidadeTotalOleoBase", label: "Capacidade Total de Óleo de base (m³)" },
  { key: "capacidadeTotalSalmoura", label: "Capacidade Total de Salmoura (m³)" },
  { key: "capacidadeTotalLama", label: "Capacidade Total de Lama (m³)" },
  { key: "capacidadeTotalGranel", label: "Capacidade Total de Granel (m³)" },
  // Texto livre de até 255 caracteres: aparece inteiro, quebrando linha (AC3).
  { key: "comentarios", label: "Comentários" },
  { key: "atualizacaoCapacidade", label: "Data Atualização Capacidade", format: formatDetailDate },
];

/** Valor do campo pronto para a tela; `null` segue como campo sem preenchimento. */
function fieldValue(
  tanques: EmbarcacaoTanques,
  { key, format }: TanquesFieldDefinition,
): string | null {
  const value = tanques[key];
  if (value === null) return null;

  // Capacidade: só o idioma da tela é aplicado, sem arredondar nem acrescentar casas.
  if (typeof value === "number") return formatDetailNumber(value);

  return format ? format(value) : value;
}

/**
 * Seção Tanques do detalhamento da embarcação (US-1796).
 *
 * Estruturada exatamente como as demais seções: um título, uma data de última
 * atualização no cabeçalho e uma lista contínua de campos em duas colunas
 * (AC2). Exclusivamente de leitura: nenhum botão ou campo de cadastro, edição
 * ou exclusão.
 */
export function TanquesSection({ embarcacaoId, onReauthenticate }: EmbarcacaoDetailSectionProps) {
  const { status, tanques, errorMessage, reload } = useEmbarcacaoTanques(embarcacaoId);

  return (
    <DetailSection
      id={TANQUES_ID}
      title={TANQUES_TITLE}
      // Só há data de atualização a exibir quando a seção tem dados.
      ultimaAtualizacao={tanques ? tanques.ultimaAtualizacao : undefined}
      testId={TEST_ID}
    >
      {tanques ? (
        <DetailSectionFields testId={`${TEST_ID}-fields`}>
          {TANQUES_FIELDS.map((field) => (
            <DetailField
              key={field.key}
              label={field.label}
              value={fieldValue(tanques, field)}
              testId={`${TEST_ID}-${field.key}`}
            />
          ))}
        </DetailSectionFields>
      ) : (
        <DetailSectionState
          // `success` sempre traz os campos: aqui só chegam carregamento e exceções.
          status={status === "success" ? "error" : status}
          loadingMessage={TANQUES_LOADING_MESSAGE}
          errorMessage={errorMessage}
          emptyContent={TANQUES_EMPTY_STATE}
          onReauthenticate={onReauthenticate}
          onRetry={reload}
          testId={TEST_ID}
        />
      )}
    </DetailSection>
  );
}
